import { CreateCourse, DuplicateCourse, GetCourses } from '@/connection/courses';
import { useState } from 'react';
import { ICourse } from '@/types/courses';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import Modal from '@ui/modal/modal';
import OptionalComponent from '@ui/optional-component/optional-component';
import WaitingIndicator from '@ui/waiting-indicator/waiting-indicator';
import CourseBadge from '@/micros/edit-project/grid-areas/main/courses/components/icons/course-badge';
import { useAppSelector } from '@/store/hooks';
import { getUser } from '@/store/slices/user/selectors';
import { getWorkingProject } from '@/store/slices/project/selectors';
import FolderPaths from '@/utils/images';
import toastTrigger from '@ui/toast/toast-trigger';
import Popover from '@ui/popover/popover';
import Text from '@ui/text/text/text';
import Button from '@ui/buttons/button/button';
import Container from '@ui/container/container';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import { EColorVariablesV2 } from '@/types/theme-packs.types';

type Props = {
  setCourse: React.Dispatch<React.SetStateAction<ICourse | null>>;
};

export default function NoCourse({ setCourse }: Props) {
  const user = useAppSelector(getUser);
  const workingEvent = useAppSelector(getWorkingProject);

  const [showAddFromLibraryModal, setShowAddFromLibraryModal] = useState(false);
  const [selectedLibraryCourse, setSelectedLibraryCourse] = useState<ICourse | null>(null);
  const [loadingLibrary, setLoadingLibrary] = useState(true);
  const [libraryCourses, setLibraryCourses] = useState<ICourse[]>([]);

  const loadAllCourses = async () => {
    if (!user) return;

    try {
      const courses = await GetCourses(user.active_channel);
      setLibraryCourses(courses);
    } catch {
      toastTrigger(<div className="S1">Error fetching courses library</div>, {
        type: 'error',
        autoClose: 5000,
      });
    }

    setLoadingLibrary(false);
  };

  const initializeCourse = async () => {
    if (!user || !workingEvent || !courseCanBeAddedToEvent()) return;

    try {
      const newCourse = await CreateCourse(user.active_channel, {
        event_id: workingEvent.event,
        channel: user.active_channel,
        name: {
          base: '',
          changed: '',
        },
        created_by: 0, // Backend will handle created_by, just required by typescript
        credits: 0,
        categories: [],
      });

      setCourse(newCourse);
      setShowAddFromLibraryModal(false);
    } catch {
      toastTrigger(<div className="S1">Error initializing new course</div>, {
        type: 'error',
        autoClose: 5000,
      });
    }
  };

  const addCourseFromLibrary = async () => {
    if (!user?.active_channel || !workingEvent?.event || !selectedLibraryCourse) return;

    try {
      const duplicatedCourse = await DuplicateCourse(
        user?.active_channel,
        workingEvent?.event,
        selectedLibraryCourse?.uuid
      );
      setCourse(duplicatedCourse);
      setShowAddFromLibraryModal(false);

      if (workingEvent?.sessions.length > 1) {
        // Requirements are tied to a specific session, so if existing event has multiple session the cloned requirements
        // will just be all assigned to the first session and the admin will need to update that since we can't
        // magically do it for them
        toastTrigger(
          <div className="S1">Your event has multiple sessions please update your requirements accordingly</div>,
          {
            type: 'warning',
            autoClose: 50000,
          }
        );
      }
    } catch {
      toastTrigger(<div className="S1">Error adding course from library</div>, {
        type: 'error',
        autoClose: 5000,
      });
    }
  };

  const courseCanBeAddedToEvent = (): boolean => {
    const coursesCanbeAdded = !!workingEvent && workingEvent.sessions.length === 1;
    if (!coursesCanbeAdded) {
      toastTrigger(
        <div className="S1">
          Course Project
          <span>Courses are limited to single session projects</span>
        </div>,
        {
          type: 'warning',
          autoClose: 5000,
        }
      );
    }

    return coursesCanbeAdded;
  };

  const handleAddCourseFromLibraryButton = () => {
    if (!courseCanBeAddedToEvent()) {
      return;
    }
    loadAllCourses();
    setShowAddFromLibraryModal(true);
  };

  return (
    <>
      <Container
        className="flex-column ai-center jc-center gap-20 padding-t-100"
        data-testid="course-create"
        style={{
          minHeight: '400px',
        }}
      >
        <img
          className="position-absolute"
          src={FolderPaths.PinkCircularGradient}
          style={{
            height: '200px',
            width: '200px',
            transform: 'translate(-80px, -125px)',
          }}
        />
        <img src={FolderPaths.TellyDancePose} />
        <h1>No course added</h1>
        <Popover
          backgroundColor="var(--gray-700)"
          cycleWithArrowKeys
          contentClassName="margin-t-10"
          renderTriggerButton={() => (
            <Button
              className="padding-v-16 padding-h-24 flex gap-10 ai-center jc-center"
              backgroundColor={getThemeColorCSSVariable()}
            >
              <Text
                fontSize="14px"
                className="L2"
                color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
              >
                Configure Course
              </Text>
              <div>
                <Icon
                  name={ICONS.KEYBOARD_ARROW_DOWN}
                  color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
                />
              </div>
            </Button>
          )}
        >
          <Button
            hoverBackgroundColor="var(--gray-600)"
            outlineOnFocus={false}
            className="radius-0 text-left padding-v-10 padding-h-16 width-100-percent flex ai-center gap-10"
            onClick={initializeCourse}
          >
            <Icon name={ICONS.ADD} size={12}></Icon>
            <Text color="var(--gray-100)" className="P2">
              New Course
            </Text>
          </Button>
          <Button
            hoverBackgroundColor="var(--gray-600)"
            outlineOnFocus={false}
            className="radius-0 text-left padding-v-10 padding-h-16 width-100-percent flex ai-center gap-10"
            onClick={handleAddCourseFromLibraryButton}
          >
            <Icon name={ICONS.FOLDER} size={12}></Icon>
            <Text color="var(--gray-100)" className="P2">
              From Library
            </Text>
          </Button>
        </Popover>
      </Container>
      <Modal
        open={showAddFromLibraryModal}
        customSize={{
          width: 'fit-content',
          height: 'auto',
          maxHeight: '600px',
          minHeight: '200px',
          maxWidth: 'calc(100vw - 80px)',
          minWidth: '690px',
        }}
        contentWrapperClassName="padding-24 gap-24"
        onRequestClose={() => setShowAddFromLibraryModal(false)}
        renderHeaderContent={() => {
          return (
            <Container>
              <Text clamp={2} whiteSpace="wrap" tag="h3" color="var(--gray-100)" className="H4 margin-b-8">
                Add course from library
              </Text>
              <Text color="var(--gray-400)" className="P1">
                Courses from projects that have been saved as templates.
              </Text>
            </Container>
          );
        }}
        renderFooterContent={() => {
          return (
            <Container className="flex jc-flex-end ai-center">
              <button
                className="L1 padding-v-8 padding-h-16 margin-r-8"
                onClick={() => {
                  setShowAddFromLibraryModal(false);
                  setSelectedLibraryCourse(null);
                }}
              >
                Cancel
              </button>
              <OptionalComponent display={!!libraryCourses.length}>
                <Button
                  width="105px"
                  className="flex ai-center padding-v-8 padding-h-16"
                  onClick={addCourseFromLibrary}
                  disabled={!selectedLibraryCourse}
                  backgroundColor={getThemeColorCSSVariable()}
                >
                  <Text
                    color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
                    className="L1"
                  >
                    Save
                  </Text>
                </Button>
              </OptionalComponent>
              <OptionalComponent display={!libraryCourses.length}>
                <Button
                  width="105px"
                  className="flex ai-center padding-v-8 padding-h-16"
                  onClick={initializeCourse}
                  backgroundColor={getThemeColorCSSVariable()}
                >
                  <Text
                    whiteSpace="nowrap"
                    className="L1"
                    color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
                  >
                    Create new
                  </Text>
                </Button>
              </OptionalComponent>
            </Container>
          );
        }}
      >
        <>
          <OptionalComponent display={loadingLibrary}>
            <div className="flex ai-center jc-center height-60">
              <WaitingIndicator />
            </div>
          </OptionalComponent>
          <OptionalComponent display={!libraryCourses.length && !loadingLibrary}>
            <Container className="flex ai-center flex-column padding-40 gap-10">
              <Text className="H4">Library empty</Text>
              <Text className="P1">Create a new course from scratch</Text>
            </Container>
          </OptionalComponent>
          <OptionalComponent display={!!libraryCourses.length && !loadingLibrary}>
            <Container
              background="var(--gray-800)"
              className="create-courses-container margin-1"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '8px',
              }}
            >
              {libraryCourses.map((course, idx) => {
                const badgeColors =
                  idx % 2 === 0 ? { start: '#5AE4FF', end: '#A7FF7B' } : { start: '#FF6AE6', end: '#55D6F0' };

                const cardColors =
                  idx % 2 === 0
                    ? { start: 'rgb(241, 115, 230, 0.1)', end: 'rgb(118, 194, 238, 0.1)' }
                    : { start: 'rgb(95, 219, 229, 0.1)', end: 'rgb(146, 238, 139, 0.1)' };

                return (
                  <Button
                    height="117px"
                    width="208px"
                    key={course.uuid}
                    className="flex-column ai-flex-start jc-space-between gap-12 padding-8 radius-12"
                    style={{
                      background: `linear-gradient(to right, ${cardColors.start} , ${cardColors.end})`,
                      border:
                        selectedLibraryCourse?.uuid === course.uuid
                          ? 'solid 2px var(--gray-200)'
                          : 'solid 2px transparent',
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      setSelectedLibraryCourse(
                        selectedLibraryCourse && selectedLibraryCourse.uuid === course.uuid ? null : course
                      );
                    }}
                  >
                    <div>
                      <div className="position-absolute">
                        <CourseBadge height={32} width={32} startColor={badgeColors.start} endColor={badgeColors.end} />
                      </div>
                      <div className="position-absolute top-13 left-19">
                        <Icon name={ICONS.TROPHY_FILLED} size={10} color="var(--gray-1200)" />
                      </div>
                    </div>
                    <div className="flex-column gap-4">
                      <Text className="C1" color="var--gray-200)">
                        {course.requirements?.length || '0'}{' '}
                        {course.requirements?.length === 1 ? 'requirement' : 'requirements'}
                      </Text>
                      <Text className="H6">{course.name.base}</Text>
                    </div>
                  </Button>
                );
              })}
            </Container>
          </OptionalComponent>
        </>
      </Modal>
    </>
  );
}
